/**
 * Seasonal events — a time-boxed lesson that appears on the menu for a few weeks and is gone again.
 *
 * Pure and Phaser-free so the window arithmetic is unit-tested. Everything is decided on the
 * device's LOCAL calendar date and nothing is fetched, so an event works offline and needs no
 * deploy to switch on or off: the content ships in the bundle and the dates open and close it.
 *
 * An event is more than a lesson, so this module owns the three decisions the rest of the app asks:
 *  - is the event on (window + the player's language has the lesson)?
 *  - did this launch arrive through its store card (`?event=halloween`)?
 *  - has the player already been welcomed to it?
 *
 * Next year's window is one more entry in EVENT_WINDOWS; the lesson, art and strings stay.
 */
import { devParam, urlParam } from './dev';

export type EventId = 'halloween';

export interface EventWindow {
  id: EventId;
  /** First and last day, inclusive, as local `YYYY-MM-DD`. */
  start: string;
  end: string;
}

/** Halloween 2026 is Saturday 31 Oct — 10 days before it and 10 after, 21 days in all. */
export const EVENT_WINDOWS: readonly EventWindow[] = [
  { id: 'halloween', start: '2026-10-21', end: '2026-11-10' },
];

/** Lesson (pool id) each event adds to the picker. */
export const EVENT_POOL_ID: Record<EventId, string> = { halloween: 'halloween' };

/** Languages that have the event's lesson — the store event and the lesson are English + German. */
const EVENT_LANGS: Record<EventId, readonly string[]> = { halloween: ['en', 'de', 'de-CH'] };

function localYmd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** The window of an event that is open on `now`, if any. */
export function windowAt(id: EventId, now: Date): EventWindow | null {
  const today = localYmd(now);
  return EVENT_WINDOWS.find((w) => w.id === id && w.start <= today && today <= w.end) ?? null;
}

/** Whole days left including today (last day → 1), 0 when the event is not on. */
export function daysLeft(id: EventId, now: Date): number {
  const w = windowAt(id, now);
  if (!w) return 0;
  const [y, m, d] = w.end.split('-').map(Number);
  const endDay = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((endDay - today) / 86_400_000) + 1;
}

export function eventHasLang(id: EventId, lang: string): boolean {
  return EVENT_LANGS[id].includes(lang);
}

/**
 * True when the event is on for this language. The `?event=<id>` dev flag forces it on (dev and
 * staging hosts only — devParam is inert in production) so the lesson can be looked at, and
 * photographed for the stores, outside its three weeks.
 */
export function eventActive(id: EventId, lang: string, now: Date = new Date()): boolean {
  if (!eventHasLang(id, lang)) return false;
  if (devParam('event') === id) return true;
  return windowAt(id, now) !== null;
}

/** The event that is on for this language, if any. */
export function activeEvent(lang: string, now: Date = new Date()): EventId | null {
  return EVENT_WINDOWS.map((w) => w.id).find((id) => eventActive(id, lang, now)) ?? null;
}

/**
 * The event a store card named by launching the app with `?event=<id>`, read ONCE when the module
 * loads. Android passes the query on the launch navigation and the iOS shell appends it to the
 * base URL, so by the time anything asks it is gone from the address bar's point of view — reading
 * it at import is what keeps it.
 *
 * Only the event's id is read, never anything the player could be moved by: the link opens the
 * welcome card and the player taps Start themself, so no URL can change a paying player's lesson
 * (see urlLesson in packs.ts for why that matters).
 */
const LAUNCH_EVENT: string | null = urlParam('event');
let launchEventConsumed = false;

export function consumeLaunchEvent(): EventId | null {
  if (launchEventConsumed) return null;
  launchEventConsumed = true;
  return EVENT_WINDOWS.some((w) => w.id === LAUNCH_EVENT) ? (LAUNCH_EVENT as EventId) : null;
}

const SEEN_KEY = (id: EventId, year: number): string => `abcs.event.${id}-${year}.v1`;

/** Whether the welcome card for this event (this year's run) has been shown. Never throws. */
export function eventWelcomed(id: EventId, now: Date = new Date()): boolean {
  try {
    return localStorage.getItem(SEEN_KEY(id, now.getFullYear())) === '1';
  } catch {
    return false;
  }
}

export function markEventWelcomed(id: EventId, now: Date = new Date()): void {
  try {
    localStorage.setItem(SEEN_KEY(id, now.getFullYear()), '1');
  } catch {
    /* storage unavailable (private window): the card just shows again next launch */
  }
}
